import { Injectable } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { EMPTY, interval } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { BsModalService } from 'ngx-bootstrap/modal';
import { NewVersionModalComponent } from '../shared/new-version-modal/new-version-modal.component';

interface BuildVersion {
  version: string;
  hash: string;
}

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  // version.json is a static file, so it skips InterceptorService: no auth headers,
  // and a failed poll must not open the "server unreachable" reload modal
  private http: HttpClient;
  private currentHash = '';
  private dialogOpen = false;
  private started = false;

  constructor(httpBackend: HttpBackend, private modalService: BsModalService) {
    this.http = new HttpClient(httpBackend);
  }

  initVersionCheck(intervalMs: number = 1000 * 60 * 1) {
    if (this.started) return;
    this.started = true;

    this.getVersion().subscribe(version => this.checkHash(version));

    interval(intervalMs).pipe(
      switchMap(() => this.getVersion())
    ).subscribe(version => this.checkHash(version));
  }

  private checkHash(version: BuildVersion) {
    const newHash = version?.hash;
    if (!newHash) return;

    // First successful response is the build this tab is running
    if (!this.currentHash) {
      this.currentHash = newHash;
      return;
    }

    if (this.currentHash !== newHash && !this.dialogOpen) {
      this.showNewVersionModal();
    }
  }

  private showNewVersionModal() {
    this.dialogOpen = true;
    const modalRef = this.modalService.show(NewVersionModalComponent, {
      class: 'modal-dialog-centered mxw-450 new-version-modal-dialog',
      ignoreBackdropClick: true,
      keyboard: false
    });
    // "Later" closes the modal; it reopens on the next check while the hash still differs
    modalRef.onHidden?.subscribe(() => {
      this.dialogOpen = false;
    });
  }

  private getVersion() {
    const url = `version.json?ts=${new Date().getTime()}`;
    // Swallow errors per request so one failed check (offline, 404, HTML fallback)
    // doesn't terminate the polling interval
    return this.http.get<BuildVersion>(url).pipe(
      catchError(error => {
        console.log('Error checking frontend version', error);
        return EMPTY;
      })
    );
  }
}
